import { useContext, useCallback } from 'react';

import { CrehanaI18nContext } from './Context';
import { I18nTranslation, TConfig } from './types';

interface ExtendedTConfig extends TConfig {
  count?: number;
  [key: string]: any;
}

export interface CrehanaTFunction<T = string> {
  (key: T, config?: ExtendedTConfig): string;
  (key: T, defaultValue: string, config?: ExtendedTConfig): string;
}

export interface UseTranslationResult<T, U> {
  t: CrehanaTFunction<T>;
  languageKey: U;
  debug: boolean;
  addResourceBundle: (bundle: I18nTranslation) => void;
}

const KNOWN_KEYS = ['defaultValue', 'namespace', 'interpolation', 'fallbackNs'];

function parseExtraConfig(ns?: string, config?: ExtendedTConfig): TConfig {
  let actualConfig: TConfig = {};

  if (ns) {
    actualConfig.namespace = ns;
  }

  if (config) {
    actualConfig = { ...actualConfig, ...config };
    // Get all the keys that are not part of the API
    const keys = Object.keys(config).filter(k => !KNOWN_KEYS.includes(k));

    keys.forEach(k => {
      actualConfig = {
        ...actualConfig,
        interpolation: {
          ...actualConfig.interpolation,
          [k]: config[k],
        },
      };
    });
  }

  return actualConfig;
}

/**
 * @param namespace - Optional namespace useful to be certain about which key
 * should be selected (in case there's more than one key with the same name).
 *
 * If you pass a `namespace` through the `t` function, it'll have higher priority.
 */
export function useTranslation<T extends string, U extends string = string>(
  namespace?: string,
): UseTranslationResult<T, U> | undefined {
  const context = useContext(CrehanaI18nContext);

  const tFunction = useCallback(
    (
      key: T,
      defaultValueOrC?: string | ExtendedTConfig,
      config?: ExtendedTConfig,
    ): string => {
      if (!context) {
        return key;
      }

      if (!defaultValueOrC && !namespace) {
        return context.t(key);
      }

      if (typeof defaultValueOrC === 'string') {
        return context.t(
          key,
          parseExtraConfig(namespace, {
            ...config,
            defaultValue: defaultValueOrC,
          }),
        );
      }

      return context.t(key, parseExtraConfig(namespace, defaultValueOrC));
    },
    [context, namespace],
  );

  if (!context) {
    return;
  }

  return {
    t: tFunction,
    languageKey: context.languageKey as U,
    debug: context.debug,
    addResourceBundle: context.addResourceBundle,
  };
}
